'use client';

import StatusBadge from '@/components/common/StatusBadge/StatusBadge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import dayjs from 'dayjs';
import { Clock, MoreHorizontal } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

interface SessionCardProps {
  title: string;
  program: string;
  id: string;
  trainer: string;
  trainerImage: string;
  isCompleted?: boolean;
  sessionId: string;
  sessionStart: string;
  sessionEnd: string;
}

export default function SessionCard({
  title,
  program,
  trainer,
  trainerImage,
  sessionId,
  sessionEnd,
  sessionStart,
  id,
}: SessionCardProps) {
  const navigate = useNavigate();
  return (
    <Card
      className='flex flex-col md:flex-row items-center gap-4 rounded-xl p-4 bg-gradient-to-br from-[rgba(47,47,47,0.44)] to-[rgba(47,47,47,0.44)] border-0'
      style={{
        background:
          'linear-gradient(154.36deg, rgba(47, 47, 47, 0.44) 11.3%, rgba(47, 47, 47, 0.44) 95.38%)',
      }}
    >
      <div className='flex gap-2 items-center w-full'>
        <div className='flex gap-2'>
          <div className='flex flex-col items-center justify-center w-12 h-12 bg-emerald-700 text-white rounded-full p-2'>
            <span className='text-[10px] font-semibold'>{dayjs(sessionStart).format('MMM')}</span>
            <span className='text-base font-semibold'>{dayjs(sessionStart).format('DD')}</span>
          </div>
          <Avatar className='h-12 w-12'>
            <AvatarImage src={trainerImage} alt={trainer} />
            <AvatarFallback className='bg-bgSecondary text-white'>
              {trainer.charAt(0) || 'T'}
            </AvatarFallback>
          </Avatar>
        </div>
        {/* Content */}
        <div className='flex-1'>
          <div className='flex items-center justify-between'>
            <div>
              <span className='text-xs text-gray-400'>#{sessionId}</span>
              <h3 className='text-base font-medium text-white'>
                {program === 'FitPrime' ? program + `(${title})` : program}{' '}
                {trainer ? (
                  <>
                    <span className='text-gray-400'>with</span> {trainer || ''}
                  </>
                ) : (
                  ''
                )}
              </h3>
              <div className='flex items-center gap-2 mt-1'>
                <Link
                  id='add-review'
                  to={`/feedback/${id}`}
                  className='flex items-center text-sm px-3 py-0 text-white bg-bgSecondary rounded-full h-8'
                >
                  + Add Review
                </Link>
              </div>
            </div>
            <div className='hidden md:flex items-center gap-2 h-full'>
              <div className='flex items-center gap-2 text-sm text-gray-300'>
                <Clock className='h-4 w-4' />
                <span>
                  {sessionStart ? dayjs(sessionStart).format('MMM DD YYYY | h:mm a') : ''} -{' '}
                  {sessionEnd ? dayjs(sessionEnd).format('h:mm a') : ''}
                </span>
              </div>
              <StatusBadge status={'Completed'} />
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    id='more-btn'
                    variant='ghost'
                    size='icon'
                    className='text-gray-400 hover:text-white'
                  >
                    <MoreHorizontal className='h-5 w-5' />
                  </Button>
                </PopoverTrigger>
                <PopoverContent align='center' className='p-2 w-40 border-none bg-gray-800'>
                  <ul className='text-sm text-white rounded-sm'>
                    <li
                      className='p-2 hover:bg-zinc-900 cursor-pointer rounded-sm'
                      onClick={() => navigate(`/session/${id}`)}
                    >
                      View
                    </li>
                  </ul>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </div>
      </div>
      <div className='flex md:hidden items-center gap-2 h-full'>
        <div className='flex items-center gap-2 text-sm text-gray-300'>
          <Clock className='h-4 w-4' />
          <span>
            {sessionStart ? dayjs(sessionStart).format('MMM DD YYYY | h:mm a') : ''} -{' '}
            {sessionEnd ? dayjs(sessionEnd).format('h:mm a') : ''}
          </span>
        </div>
        <StatusBadge status={'Completed'} />
        <Popover>
          <PopoverTrigger asChild>
            <Button
              id='more-btn'
              variant='ghost'
              size='icon'
              className='text-gray-400 hover:text-white'
            >
              <MoreHorizontal className='h-5 w-5' />
            </Button>
          </PopoverTrigger>
          <PopoverContent align='center' className='p-2 w-40 border-none bg-gray-800'>
            <ul className='text-sm text-white rounded-sm'>
              <li
                className='p-2 hover:bg-zinc-900 cursor-pointer rounded-sm'
                onClick={() => navigate(`/session/${id}`)}
              >
                View
              </li>
            </ul>
          </PopoverContent>
        </Popover>
      </div>
    </Card>
  );
}
